/**
 * Firebase Configuration
 * Initializes Firebase app and Cloud Messaging for push notifications
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

// Cloud Messaging may not be available in all browsers (e.g. iOS Safari/Chrome or
// when not served over HTTPS).  Only create the instance after verifying support.
let _messaging = null;

/**
 * Return a messaging instance or null if the current browser does not support it.
 * Using the SDK helper to avoid runtime errors on unsupported platforms.
 */
async function getFirebaseMessaging() {
  if (_messaging !== null) {
    return _messaging;
  }

  if (await isSupported()) {
    _messaging = getMessaging(app);
  } else {
    _messaging = null;
  }

  return _messaging;
}

/**
 * Request notification permission and retrieve FCM token
 * @returns {Promise<string|null>} FCM token or null if denied
 */
export const requestNotificationPermission = async () => {
  // if browser doesn’t support messaging, exit cleanly so UI can continue
  if (!(await isSupported())) {
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return null;
    }

    const swUrl = `/firebase-messaging-sw.js?apiKey=${firebaseConfig.apiKey}&authDomain=${firebaseConfig.authDomain}&projectId=${firebaseConfig.projectId}&storageBucket=${firebaseConfig.storageBucket}&messagingSenderId=${firebaseConfig.messagingSenderId}&appId=${firebaseConfig.appId}`;

    const registration = await navigator.serviceWorker.register(swUrl);

    const msg = await getFirebaseMessaging();
    if (!msg) {
      return null;
    }

    const token = await getToken(msg, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    return token;
  } catch (error) {
    console.debug('requestNotificationPermission failed', error);
    return null;
  }
};

// expose helpers; consumers can import onMessage and call getFirebaseMessaging()
export { onMessage, getFirebaseMessaging };
